import { Button } from "@devhop/ui";
import { useNavigate, useParams } from "@tanstack/react-router";
import { ArrowLeftIcon, HelpCircle, PrinterIcon } from "lucide-react";
import { useEffect } from "react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { useMeetingByIdQuery } from "../api/queries";
import { MeetingPdfPreviewContent } from "../ui/MeetingPdfPreviewContent";
import {
  MeetingTour,
  meetingPdfPreviewTourSteps,
  useMeetingTour,
} from "../tour";

export function MeetingPdfPreviewPage() {
  const params = useParams({ strict: false });
  const id = params?.id as string | undefined;
  const nav = useNavigate();

  const { data: meeting, isLoading, error } = useMeetingByIdQuery(id, Boolean(id));
  const tour = useMeetingTour("pdfPreview");

  // Set document title to meeting title so "Save as PDF" uses it as filename
  useEffect(() => {
    if (meeting?.title && id) {
      document.title = meeting.title;
    }
    return () => {
      document.title = "freedom® Admin";
    };
  }, [meeting?.title, id]);

  const handleBack = () => {
    nav({ to: "/app/management/meetings/$id", params: { id: id ?? "" } });
  };

  const handlePrint = () => {
    window.print();
  };

  if (isLoading || !id) {
    return (
      <>
        <Header />
        <Main>
          <div className="flex min-h-[400px] items-center justify-center">
            <div className="text-center">
              <div className="mx-auto h-8 w-8 animate-spin rounded-full border-primary border-b-2" />
              <p className="mt-2 text-muted-foreground">ກໍາລັງໂຫຼດ...</p>
            </div>
          </div>
        </Main>
      </>
    );
  }

  if (error || !meeting) {
    return (
      <>
        <Header />
        <Main>
          <div className="flex min-h-[400px] items-center justify-center">
            <div className="text-center">
              <h2 className="mb-2 font-bold text-2xl text-destructive">
                ບໍ່ພົບບັນທຶກການປະຊຸມ
              </h2>
              <p className="mb-4 text-muted-foreground">
                ບັນທຶກການປະຊຸມທີ່ທ່ານຊອກຫາບໍ່ມີຢູ່ ຫຼື ຖືກລຶບໄປແລ້ວ
              </p>
              <Button onClick={handleBack}>
                <ArrowLeftIcon className="mr-2 h-4 w-4" />
                ກັບໄປທີ່ບັນທຶກການປະຊຸມ
              </Button>
            </div>
          </div>
        </Main>
      </>
    );
  }

  return (
    <>
      <Header />
      <Main>
        <MeetingTour
          steps={meetingPdfPreviewTourSteps}
          run={tour.run}
          onCallback={tour.handleJoyrideCallback}
        />
        {/* Header Actions - Hidden in print */}
        <div
          className="mb-6 flex items-center justify-between print:hidden"
          data-tourid="meeting-pdf-header"
        >
          <div className="flex items-center gap-2">
            <Button variant="ghost" onClick={handleBack} className="gap-2">
              <ArrowLeftIcon className="h-4 w-4" />
              ກັບໄປທີ່ບັນທຶກການປະຊຸມ
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="size-7 rounded-full"
              onClick={tour.startTour}
              title="ເລີ່ມທົດສອບການນຳທາງ"
              aria-label="ເລີ່ມທົດສອບການນຳທາງ"
            >
              <HelpCircle className="size-5" />
            </Button>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={handlePrint} className="gap-2">
              <PrinterIcon className="h-4 w-4" />
              ພິມ / ບັນທຶກເປັນ PDF
            </Button>
          </div>
        </div>

        {/* PDF Preview Content */}
        <div data-tourid="meeting-pdf-content">
          <MeetingPdfPreviewContent meeting={meeting} />
        </div>
      </Main>
    </>
  );
}
